import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { fmtCents } from '@/components/money/format.js';
import StatementMatching from '@/components/money/StatementMatching.jsx';
import { PaymentSettlements, DueDateEntry } from '@/components/money/PaymentSettlement.jsx';

// STATEMENTS (TRACON-MONEY-SPEC §8): what each carrier says is due, what went
// to it, and the open balance (the view, R29). Lines are matched to deals only
// by confirmation (V4): TRACON proposes each item's deal (NIGHT-13 R34), the
// exact ones under one button, the rest one per row with the reason in words.
// Hand entry is a first-class path (§8.2): the statement and its lines as the
// document states them, typed into empty fields. Nothing here computes a line.

const selectCls = 'mt-1 h-11 w-full rounded-card border border-border bg-bg px-2 text-sm text-text';
const emptyLine = () => ({ insured_text: '', policy_text: '', effective_date: '', gross: '', commission: '', tax: '', net_due: '' });

const DISPOSITION_WORDS = { not_payable: 'Not a payable', superseded: 'Replaced by a later statement', paid_no_line: 'Paid from an account with no bank export' };

// A transaction a line is confirmed to, in words: type, effective day, its description (David 2026-11-03: never "null").
function TxnWords({ t, id }) {
  if (!t) return <span className="font-mono" title={id || ''}>a transaction TRACON cannot read ({id ? String(id).slice(0, 8) : 'none'})</span>;
  const d = t.description && t.description.length > 60 ? `${t.description.slice(0, 57)}...` : t.description;
  return <span className="text-text" title={`${t.description || ''} (${id})`}>{t.type}{t.effective_date ? ` eff ${t.effective_date}` : ''}{d ? ` (${d})` : ''}</span>;
}

export default function MoneyStatements() {
  const [statements, setStatements] = useState(null);
  const [payees, setPayees] = useState([]);
  const [unpaid, setUnpaid] = useState([]);
  const [proposals, setProposals] = useState(new Map());
  const [settlements, setSettlements] = useState([]);
  const [error, setError] = useState('');
  // Money this week's "Open the statement" names the statement to open (R36: every item links to the door that acts on it).
  const [params] = useSearchParams();
  const [open, setOpen] = useState(params.get('statement') || null);
  const [showEntry, setShowEntry] = useState(false);

  // Hand entry. Every field starts EMPTY (V4, guard tests/sweep-no-prefill).
  const [carrier, setCarrier] = useState('');
  const [systemLabel, setSystemLabel] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [statedTotal, setStatedTotal] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, p, u, m, st] = await Promise.all([api.get('/api/money/statements'), api.get('/api/money/pay/payees'), api.get('/api/money/pay/unpaid'), api.get('/api/money/statements/proposals'), api.get('/api/money/statements/settlements')]);
      setStatements(s.statements || []);
      setPayees(p.payees || []);
      setUnpaid(u.rows || []);
      setProposals(new Map((m.statements || []).map((x) => [x.statement_id, x])));
      setSettlements(st.payments || []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load statements');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const linesTotal = Math.round(lines.reduce((acc, l) => acc + (Number(l.net_due) || 0), 0) * 100) / 100;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        carrier_id: Number(carrier),
        system_label: systemLabel || null,
        period_end: periodEnd,
        statement_date: statementDate,
        due_date: dueDate || null,
        stated_total: Number(statedTotal),
        lines: lines.filter((l) => l.insured_text.trim() || l.net_due !== '').map((l) => ({
          insured_text: l.insured_text, policy_text: l.policy_text || null, effective_date: l.effective_date || null,
          gross: l.gross === '' ? null : Number(l.gross), commission: l.commission === '' ? null : Number(l.commission),
          tax: l.tax === '' ? null : Number(l.tax), net_due: Number(l.net_due),
        })),
      };
      const r = await api.post('/api/money/statements', body);
      toast.success(`Statement entered: stated ${fmtCents(r.statement.stated_total)}, ${r.lines.length} line(s)${r.difference !== 0 ? `, lines differ from the stated total by ${fmtCents(r.difference)}` : ''}.`);
      setCarrier(''); setSystemLabel(''); setPeriodEnd(''); setStatementDate(''); setDueDate(''); setStatedTotal(''); setLines([emptyLine()]);
      setShowEntry(false);
      await refresh();
    } catch (e) {
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not enter the statement');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = carrier && periodEnd && statementDate && statedTotal !== '' && !busy;

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-text">Statements</h2>
          <p className="mt-1 text-sm text-text-muted">
            What each carrier says is due, less what was paid against it. TRACON proposes the deal for each line; a line counts only when you confirm it.
          </p>
        </div>
        <Button variant="outline" onClick={() => setShowEntry((v) => !v)}>{showEntry ? 'Close entry' : 'Enter a statement by hand'}</Button>
      </div>

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {statements === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}

      {showEntry ? (
        <div className="mt-4 rounded-card border border-border bg-surface p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-xs text-text-muted">
              Carrier
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className={selectCls}>
                <option value="">choose carrier</option>
                {payees.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-text-muted">System label (a carrier with two systems)<Input value={systemLabel} onChange={(e) => setSystemLabel(e.target.value)} className="mt-1" /></label>
            <label className="text-xs text-text-muted">Stated total<Input type="number" step="0.01" inputMode="decimal" data-money-input="stated_total" value={statedTotal} onChange={(e) => setStatedTotal(e.target.value)} className="mt-1" /></label>
            <label className="text-xs text-text-muted">Period end<Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="mt-1" /></label>
            <label className="text-xs text-text-muted">Statement date<Input type="date" value={statementDate} onChange={(e) => setStatementDate(e.target.value)} className="mt-1" /></label>
            <label className="text-xs text-text-muted">Due date<Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" /></label>
          </div>
          <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-text-dim">Lines, as the document states them</div>
          <div className="mt-2 space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid gap-2 md:grid-cols-7">
                <Input placeholder="insured" value={l.insured_text} onChange={(e) => setLine(i, 'insured_text', e.target.value)} className="md:col-span-2" />
                <Input placeholder="policy" value={l.policy_text} onChange={(e) => setLine(i, 'policy_text', e.target.value)} />
                <Input type="date" value={l.effective_date} onChange={(e) => setLine(i, 'effective_date', e.target.value)} />
                <Input type="number" step="0.01" placeholder="gross" value={l.gross} onChange={(e) => setLine(i, 'gross', e.target.value)} />
                <Input type="number" step="0.01" placeholder="commission" value={l.commission} onChange={(e) => setLine(i, 'commission', e.target.value)} />
                <Input type="number" step="0.01" placeholder="net due" value={l.net_due} onChange={(e) => setLine(i, 'net_due', e.target.value)} />
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={() => setLines((ls) => [...ls, emptyLine()])}>Add line</Button>
            <span className="font-mono text-xs text-text-muted">lines total {fmtCents(linesTotal)}{statedTotal !== '' && linesTotal !== Number(statedTotal) ? <span className="text-danger"> · differs from the stated total by {fmtCents(linesTotal - Number(statedTotal))}</span> : null}</span>
            <Button onClick={submit} disabled={!canSubmit} className="ml-auto">{busy ? 'Entering...' : 'Enter statement'}</Button>
          </div>
        </div>
      ) : null}

      {statements ? (
        statements.length === 0 ? <p className="mt-4 text-sm text-text-muted">No statements on file.</p> : (
          <ul className="mt-4 divide-y divide-border rounded-card border border-border bg-surface px-3">
            {statements.map((s) => (
              <li key={s.statement_id} className="py-3">
                <button type="button" onClick={() => setOpen(open === s.statement_id ? null : s.statement_id)} className="flex w-full flex-wrap items-baseline justify-between gap-2 text-left">
                  <span className="text-sm font-medium text-text">{s.carrier_name || s.carrier_id}{s.system_label ? ` · ${s.system_label}` : ''} <span className="text-xs text-text-dim">period end {s.period_end} · statement {s.statement_date}{s.due_date ? ` · due ${s.due_date}` : ''}</span></span>
                  <span className="font-mono text-sm">
                    <span className="text-text-muted">stated {fmtCents(s.stated_total)}{Number(s.carried_forward) ? ` · carried forward ${fmtCents(s.carried_forward)}` : ''} · paid {fmtCents(s.paid_by_links)} ({s.link_count}) · </span>
                    <span className={Number(s.open_balance) === 0 ? 'text-text-dim' : 'text-text'}>open {fmtCents(s.open_balance)}</span>
                  </span>
                </button>
                <div className="mt-1 font-mono text-[11.5px] text-text-muted">
                  {s.lines.length} line(s) · {s.unconfirmed_count} unconfirmed · {s.rejected_count} no such deal
                  {proposals.get(s.statement_id) ? (() => { const m = proposals.get(s.statement_id); return ` · to match: ${m.exact.length} exact, ${m.proposed.length} to check, ${m.unmatched.length} with no deal`; })() : null}{s.superseded_count ? ` · ${s.superseded_count} carried again on a later statement` : ''}
                  {s.difference !== 0 ? <span className="text-danger"> · lines differ from the stated total by {fmtCents(s.difference)}</span> : null}
                </div>
                {!s.due_date && !s.disposition && Number(s.open_balance) > 0 ? <DueDateEntry statementId={s.statement_id} onDone={refresh} /> : null}
                {s.disposition ? <div className="mt-1 text-xs text-text-muted">{DISPOSITION_WORDS[s.disposition] || s.disposition}: {s.disposition_reason}</div> : null}
                {(s.links || []).map((k) => <div key={k.id} className="mt-0.5 font-mono text-[11.5px] text-text-muted">paid {fmtCents(k.amount)}: {k.reason}</div>)}
                {open === s.statement_id ? <StatementMatching proposal={proposals.get(s.statement_id)} unpaid={unpaid} onDone={refresh} /> : null}
                {open === s.statement_id ? <PaymentSettlements payments={settlements} statementId={s.statement_id} onDone={refresh} /> : null}
                {open === s.statement_id ? (
                  <ul className="mt-2 divide-y divide-border border-t border-border">
                    {s.lines.map((l) => (
                      <li key={l.id} className="py-2 text-sm">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-mono text-xs text-text-dim">{l.line_no}</span>
                          <span className="text-text">{l.insured_text}</span>
                          {l.policy_text ? <span className="text-xs text-text-muted">{l.policy_text}</span> : null}
                          {l.effective_date ? <span className="text-xs text-text-muted">eff {l.effective_date}</span> : null}
                          <span className="ml-auto font-mono text-xs text-text">net due {fmtCents(l.net_due)}</span>
                        </div>
                        <div className="mt-1 text-xs">
                          {l.superseded_by ? (
                            <span className="text-text-dim" title={l.superseded_by.line_id}>carried again on the {l.superseded_by.period_end || 'later'} statement and counted there, not here</span>
                          ) : l.match_confidence === 'confirmed' ? (
                            <span className="text-text-muted">
                              {l.parts && l.parts.length
                                ? <>confirmed to {l.parts.length} transactions: {l.parts.map((p, i) => <span key={p.id || p.policy_transaction_id}>{i ? ' + ' : ''}<TxnWords t={p.txn} id={p.policy_transaction_id} /> <span className="font-mono">{fmtCents(p.net_due)}</span></span>)}</>
                                : <>confirmed to <TxnWords t={l.txn} id={l.policy_transaction_id} /></>}
                              {l.together
                                ? <span className="ml-2 font-mono text-[11.5px]">lines {l.together.line_nos.join(' + ')} together: carrier commission {fmtCents(l.together.commission)} / derived {fmtCents(l.derived_commission)} · carrier net {fmtCents(l.together.net_due)} / derived {fmtCents(l.derived_net)}</span>
                                : <span className="ml-2 font-mono text-[11.5px]">carrier commission {fmtCents(l.commission)} / derived {fmtCents(l.derived_commission)} · carrier net {fmtCents(l.net_due)} / derived {fmtCents(l.derived_net)}</span>}
                              {l.delta_reason ? <span className="ml-2 text-danger">differs by {fmtCents(l.delta_net)} net, {fmtCents(l.delta_commission)} commission: {l.delta_reason}</span> : <span className="ml-2">same</span>}
                            </span>
                          )
                            : l.match_confidence === 'rejected' ? <span className="text-danger">no such deal</span>
                              : <span className="text-text-dim">not matched yet: decide it in the list above</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
